/**
 * api/assistant.ts —— AI 助手（06 §五 5.13）
 *
 * ⚠ 本文件是全站**唯一直接用 fetch 的 api 模块**，破了「业务代码不许直接 fetch」那条纪律里
 *   的一半——原因是 `client.ts` 的 `req()` 把响应体一次读成文本再 JSON.parse，
 *   对流式接口不适用（那样等于等全部输出完再显示，流式就白做了）。
 * ⚠ 但**只破这一处**：`bootstrap` 与 `detail` 照常走 `get()`，不要顺手也改成裸 fetch。
 */
import type {
  AiConversationDetail,
  AiFeedback,
  AiFeedbackResult,
  AiStreamEvent,
  AssistantBootstrap,
} from '@shared-types';
import { apiBase, get, put } from './client.js';

export const bootstrap = (): Promise<AssistantBootstrap> =>
  get<AssistantBootstrap>('/api/assistant/bootstrap');

export const detail = (id: string): Promise<AiConversationDetail> =>
  get<AiConversationDetail>(`/api/assistant/conversations/${encodeURIComponent(id)}`);

/** 回答反馈（2026-09-23）。`null` = 取消 */
export const feedback = (messageId: string, value: AiFeedback | null): Promise<AiFeedbackResult> =>
  put<AiFeedbackResult>(`/api/assistant/messages/${encodeURIComponent(messageId)}/feedback`, { value });

/**
 * 流式问答。逐个事件回调，直到流结束。
 *
 * ⚠ **不抛业务异常**：后端把配额、上下文、上游三类失败都做成了 `error` 事件
 *   （见 `backend/src/types/assistant.ts` 的说明）——流一旦开始就发不了 HTTP 状态码，
 *   两套错误通道会让这里出现两条互不覆盖的分支。这里只在**网络层**真的断了时抛。
 *
 * ⚠ 解析必须**按空行切分并跨 chunk 拼接**：一个 `data:` 可能跨多行，
 *   而网络分片不保证落在事件边界上。按行切或不留 buf，症状是「偶尔丢几个字」。
 */
export async function chat(
  body: { conversationId?: string; message: string },
  onEvent: (e: AiStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`${apiBase()}/api/assistant/chat`, {
    method: 'POST',
    // ⚠ 会话是 httpOnly Cookie，漏了这行必定 401
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok || !res.body) throw new Error(`AI 服务返回 ${res.status}`);

  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let i = buf.indexOf('\n\n');
    while (i >= 0) {
      const raw = buf.slice(0, i);
      buf = buf.slice(i + 2);
      const line = raw.split('\n').find((l) => l.startsWith('data:'));
      if (line) {
        try {
          onEvent(JSON.parse(line.slice(5).trim()) as AiStreamEvent);
        } catch {
          /* 单条解析失败不中断整条流——上游的心跳/注释行不该让整次问答失败 */
        }
      }
      i = buf.indexOf('\n\n');
    }
  }
}
